import type { SetupRewardOption, SetupTaskOption } from '../services/apiService';

/**
 * New family setup (Jens 2026-10-15): which standard tasks and rewards to suggest, from the
 * family's children (with ages) and rooms. Pure, tested in familySetup.test.ts.
 * - "per_child" tasks become each child's own task in their own room (only if they are old enough);
 * - "shared" tasks are suggested once for the room they belong to, or for the whole home;
 * - rewards for "each child" are made once per child, "whole family" ones once.
 */
export const AGE_ORDER = ['age_2_3', 'age_4_5', 'age_6_7', 'age_8_9', 'age_10_12', 'age_13_plus', 'adults'];
const rank = (age: string | null | undefined) => (age && AGE_ORDER.includes(age) ? AGE_ORDER.indexOf(age) : -1);

/** Is a child of this age old enough for a task of that age group? Unknown ages fit everything. */
export function oldEnough(childAge: string | null | undefined, taskAge: string | null | undefined): boolean {
  if (!childAge || !taskAge || taskAge === 'all_ages') return true;
  return rank(childAge) >= rank(taskAge);
}

/** The catalog's room names (suggested_room_original_name) for the setup's room types. */
export const CATALOG_ROOM: Record<string, string> = {
  Kitchen: 'Kitchen',
  'Dining Room': 'Kitchen',
  'Living Room': 'Living Room',
  Bathroom: 'Bathroom',
  Toilet: 'Bathroom',
  'Laundry Room': 'Washing Room',
  Bedroom: 'Parents Bedroom',
  'Child Bedroom': 'Child Bedroom',
};

export type SetupChild = { key: string; name: string; age_group?: string | null };
/** task_room: which standard tasks belong here (from Master Admin's standard rooms); older rooms fall back to CATALOG_ROOM. */
export type SetupRoom = { key: string; name: string; original_name: string; childKey?: string | null; task_room?: string | null };
const taskRoomOf = (r: SetupRoom) => r.task_room ?? CATALOG_ROOM[r.original_name] ?? null;

export type TaskSuggestion = {
  id: string;
  option: SetupTaskOption;
  childKey: string | null;
  roomKey: string | null;
  checked: boolean;
};
export type RewardSuggestion = { id: string; option: SetupRewardOption; childKey: string | null; checked: boolean };

export function suggestTasks(options: SetupTaskOption[], children: SetupChild[], rooms: SetupRoom[]): TaskSuggestion[] {
  const out: TaskSuggestion[] = [];
  for (const o of options.filter((x) => x.starter === 'per_child')) {
    for (const c of children) {
      const own = rooms.find((r) => r.childKey === c.key);
      if (!own || !oldEnough(c.age_group, o.age_group)) continue;
      out.push({ id: `${o.template_id}:${c.key}`, option: o, childKey: c.key, roomKey: own.key, checked: o.starter_checked });
    }
  }
  for (const o of options.filter((x) => x.starter === 'shared')) {
    // Only if someone in the family is old enough (a family without children: adults can do anything).
    if (children.length && !children.some((c) => oldEnough(c.age_group, o.age_group))) continue;
    if (!o.room) {
      out.push({ id: `${o.template_id}:home`, option: o, childKey: null, roomKey: null, checked: o.starter_checked });
      continue;
    }
    const room = rooms.find((r) => !r.childKey && taskRoomOf(r) === o.room);
    if (room) out.push({ id: `${o.template_id}:${room.key}`, option: o, childKey: null, roomKey: room.key, checked: o.starter_checked });
  }
  return out;
}

export function suggestRewards(options: SetupRewardOption[], children: SetupChild[]): RewardSuggestion[] {
  const out: RewardSuggestion[] = [];
  for (const o of options) {
    if (o.target_type === 'family') out.push({ id: `${o.group_id}:family`, option: o, childKey: null, checked: o.starter_checked });
    else for (const c of children) out.push({ id: `${o.group_id}:${c.key}`, option: o, childKey: c.key, checked: o.starter_checked });
  }
  return out;
}

/** "Magnes værelse" — with the language's rule for names ending in s, x or z (e.g. "Lars' værelse"). */
export function childRoomName(name: string, t: (key: string) => string): string {
  const n = name.trim();
  const key = /[sxz]$/i.test(n) ? 'onboarding.child_room_name_s' : 'onboarding.child_room_name';
  return t(key).split('{{name}}').join(n);
}
